// "Clear inputs" in the header link row, with its confirmation dialog. Everything goes back to how
// this account or demo starts (`calculatorActions.clearInputs`): Clay-default numbers, the starting
// use case selection (demo: its three; a real account: none) and the default tool list. The account
// or demo choice stays. The store bumps `epoch`, so every draft reseeds on screen.
//
// Autosave treats the clear as any other edit, so for a linked account the dialog says what gets
// replaced. The Modal portals to <body>, outside `.vc-root`, so it uses Tailwind classes, not brand
// tokens (same as ReviewGateDialog). Cancel takes the default focus; Esc and the overlay close it.

import { useState } from 'react';
import { Button, Modal } from '../../../_shared/components';
import { calculatorActions, hasInputEditsOf, useCalculatorStore } from '../../state/calculator-store';
import { showNotice } from '../../state/notice-store';
import { Ph } from '../Icon';

export const CLEAR_TITLE = 'Clear all inputs?';
export const CLEAR_BODY =
  'Every number goes back to the Clay default (deal economics, each use case’s inputs and factors, tool spend, the contract estimate), and the use case and tool selections go back to where they started.';
export const CLEAR_KEPT =
  'Kept: the account or demo choice.';
export const clearStartLine = (demo: boolean): string =>
  demo
    ? 'Demo mode starts with CRM enrichment, Inbound speed-to-lead and Tool consolidation selected.'
    : 'No use cases will be selected.';
export const CLEAR_DEMO = 'Demo mode — nothing is saved.';
export const clearSavedLine = (accountName: string): string =>
  `This also replaces today’s saved version for ${accountName}. Earlier days are kept.`;
export const CLEAR_DONE = 'Inputs reset to Clay defaults.';

export function ClearInputsButton() {
  const [open, setOpen] = useState(false);
  const edited = useCalculatorStore(hasInputEditsOf);
  const account = useCalculatorStore((s) => s.account);

  function confirm() {
    calculatorActions.clearInputs();
    setOpen(false);
    showNotice({ tone: 'info', text: CLEAR_DONE });
  }

  return (
    <>
      <button
        type="button"
        className="vc3-linkbtn"
        disabled={!edited}
        title={edited ? 'Reset numbers and selections to where they started' : 'Nothing has been edited yet'}
        onClick={() => setOpen(true)}
      >
        <Ph name="arrow-counter-clockwise" size={13} /> Clear inputs
      </button>
      {/* No ✕ button: the Modal focuses its first focusable element on open, and that must be Cancel. Esc and the overlay still close it. */}
      <Modal
        open={open}
        title={CLEAR_TITLE}
        onClose={() => setOpen(false)}
        hideCloseButton
        footer={
          <>
            <Button variant="neutral" onClick={() => setOpen(false)} autoFocus>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirm}>
              Clear inputs
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 text-sm leading-relaxed">
          <p>{CLEAR_BODY}</p>
          <p>{clearStartLine(account?.kind === 'demo')} {CLEAR_KEPT}</p>
          {account?.kind === 'account' && <p className="font-medium">{clearSavedLine(account.accountName)}</p>}
          {account?.kind === 'demo' && <p className="text-xs opacity-70">{CLEAR_DEMO}</p>}
        </div>
      </Modal>
    </>
  );
}
